
import { COP_API_BASE_URL, getPerformanceOverviewUrl } from '../../../constants/service-endpoints'
import { get } from '../../../services/http.service'
import { getSilentToken } from '../../../services/tokenUtils'
import { type ChartVariant, resolveChartVariant } from '../utils/variantMapper'

export type ChartItem = {
  name: string
  value: number
}

type RawChartDatum = {
  name?: string
  label?: string
  indicator?: string
  value?: number | string
  count?: number | string
  data?: {
    value?: number | string
  }
}

type RawWidget = {
  indicator?: string
  type?: string
  data?:
    | {
        value?: number | string
        data?: RawChartDatum[]
      }
    | RawChartDatum[]
}

type RawSection = {
  indicator?: string
  widgets?: RawWidget[]
}

type PerformanceOverviewResponse = {
  sections?: RawSection[]
}

type PerformanceOverviewEnvelope =
  | PerformanceOverviewResponse
  | {
      data?: PerformanceOverviewResponse
    }

type CopWindow = Window & {
  __copPerformanceOverviewResponse?: PerformanceOverviewResponse
  __copChartDataByVariant?: Record<string, ChartItem[]>
}

let cachedOverviewResponse: PerformanceOverviewResponse | null = null
let cachedOverviewPromise: Promise<PerformanceOverviewResponse> | null = null

function getPerformanceOverviewRequestUrl(): Promise<string> {
  const url = `${COP_API_BASE_URL}${getPerformanceOverviewUrl()}`

  const existingToken = getSilentToken()
  if (existingToken) {
    return Promise.resolve(url)
  }

  return new Promise(resolve => {
    const handleTokenAcquired = () => {
      document.removeEventListener('cop:token-acquired', handleTokenAcquired)
      resolve(url)
    }

    document.addEventListener('cop:token-acquired', handleTokenAcquired, { once: true })
  })
}

function normalizeOverviewResponse(response: PerformanceOverviewEnvelope): PerformanceOverviewResponse {
  if (response && typeof response === 'object' && Array.isArray((response as PerformanceOverviewResponse).sections)) {
    return response as PerformanceOverviewResponse
  }

  const nestedData = (response as { data?: PerformanceOverviewResponse })?.data
  if (nestedData && Array.isArray(nestedData.sections)) {
    return nestedData
  }

  return { sections: [] }
}

function exposeOverviewResponseToDom(response: PerformanceOverviewResponse): void {
  const copWindow = window as CopWindow
  copWindow.__copPerformanceOverviewResponse = response

  document.documentElement?.setAttribute('data-cop-performance-overview-loaded', 'true')
  window.dispatchEvent(new CustomEvent('cop:performanceOverviewLoaded', { detail: response }))
}

function parseNumericValue(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function getItemName(item: RawChartDatum): string {
  return item.name || item.label || item.indicator || ''
}

function mapArrayToChartItems(items: RawChartDatum[]): ChartItem[] {
  return items
    .map(item => ({
      name: getItemName(item),
      value: parseNumericValue(item.value ?? item.count ?? item.data?.value),
    }))
    .filter(item => Boolean(item.name))
}

function getContainerItems(container: RawWidget): ChartItem[] {
  if (!container.data) {
    return []
  }

  if (Array.isArray(container.data)) {
    return mapArrayToChartItems(container.data)
  }

  if (Array.isArray(container.data.data)) {
    return mapArrayToChartItems(container.data.data)
  }

  return []
}

function getVariantForWidget(widget: RawWidget): ChartVariant {
  return resolveChartVariant(undefined, widget.indicator)
}

function isChartDataWidget(widget: RawWidget): boolean {
  if (!widget.type) {
    return true
  }

  return widget.type.trim().toLowerCase() === 'chartdata'
}

export async function fetchPerformanceOverview(): Promise<PerformanceOverviewResponse> {
  if (cachedOverviewResponse) {
    return cachedOverviewResponse
  }

  if (cachedOverviewPromise) {
    return cachedOverviewPromise
  }

  cachedOverviewPromise = (async () => {
    const url = await getPerformanceOverviewRequestUrl()
    const response = await get<PerformanceOverviewEnvelope>(url)
    const normalizedResponse = normalizeOverviewResponse(response)
    cachedOverviewResponse = normalizedResponse
    exposeOverviewResponseToDom(normalizedResponse)
    return normalizedResponse
  })()
    .finally(() => {
      cachedOverviewPromise = null
    })

  return cachedOverviewPromise
}

function extractChartItemsByVariant(response: PerformanceOverviewResponse, variant: ChartVariant): ChartItem[] {
  if (!response.sections) {
    return []
  }

  for (const section of response.sections) {
    if (!section.widgets) {
      continue
    }

    for (const widget of section.widgets) {
      
      if (!isChartDataWidget(widget)) {
        continue
      }

      if (getVariantForWidget(widget) !== variant) {
        continue
      }

      const items = getContainerItems(widget)
      if (items.length > 0) {
        return items
      }
    }
  }

  return []
}

export async function fetchChartData(variant: ChartVariant): Promise<ChartItem[]> {
  const apiData = await fetchPerformanceOverview()
  const chartData = extractChartItemsByVariant(apiData, variant)

  const copWindow = window as CopWindow
  copWindow.__copChartDataByVariant = {
    ...copWindow.__copChartDataByVariant,
    [variant]: chartData,
  }
  window.dispatchEvent(new CustomEvent('cop:chartDataLoaded', { detail: { variant, data: chartData } }))

  return chartData
}

export async function fetchMultipleChartData(variants: ChartVariant[]): Promise<Record<string, ChartItem[]>> {
  const apiData = await fetchPerformanceOverview()

  return variants.reduce(
    (acc, variant) => {
      acc[variant] = extractChartItemsByVariant(apiData, variant)
      return acc
    },
    {} as Record<string, ChartItem[]>,
  )
}

export function hasValidChartData(data: ChartItem[]): boolean {
  return data.some(item => Number.isFinite(Number(item.value)))
}
